/**
 * Quote Mode naming helpers.
 *
 * The Quote Naming rule (see F278181 MV Quote Mode DEP) is shared between the active deal
 * (see quoteSelectors.getQuoteName, which reads the Redux StateTree) and per-row consumers such
 * as the Recent Deals / Deal List, which cannot use the StateTree-bound selector. Keeping the pure
 * rule here ensures both callers format the name identically and never drift apart.
 */

export const DRAFT_QUOTE_LABEL = 'Draft quote';

const pad2 = (value: number): string => String(value).padStart(2, '0');

/**
 * Format an ISO date string as the UTC `YYYYMMDD-HHMMSS` timestamp used in quote names.
 * Returns '' for a missing or unparseable date.
 */
export const formatDealDate = (iso: string | undefined): string => {
    if (!iso) {
        return '';
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    const year = date.getUTCFullYear();
    const month = pad2(date.getUTCMonth() + 1);
    const day = pad2(date.getUTCDate());
    const hours = pad2(date.getUTCHours());
    const minutes = pad2(date.getUTCMinutes());
    const seconds = pad2(date.getUTCSeconds());
    return `${year}${month}${day}-${hours}${minutes}${seconds}`;
};

export interface BuildQuoteNameArgs {
    /** True when the record has no persisted DXG record (no dealXgId). */
    isDealXgRecordMissed: boolean;
    /** Dealer id for the `Q-{dealerId}-...` form. */
    dealerId?: number | string;
    /** ISO creation date of the deal (UTC) for the timestamp. */
    creationDate?: string;
}

/**
 * Build a Quote Mode display name from its inputs:
 * - no DXG record            -> 'Draft quote'
 * - DXG record present       -> `Q-{dealerId}-{YYYYMMDD-HHMMSS}` (UTC creation date)
 * - missing dealerId or date -> 'Draft quote' (cannot build a stable Q- name)
 *
 * This does not gate on Quote Mode itself; callers decide whether a record is a quote (e.g. the
 * feature toggle plus the absence of a customer/shopper) before formatting its name.
 */
export const buildQuoteName = ({ isDealXgRecordMissed, dealerId, creationDate }: BuildQuoteNameArgs): string => {
    if (isDealXgRecordMissed) {
        return DRAFT_QUOTE_LABEL;
    }

    const formattedDate = formatDealDate(creationDate);
    if (!dealerId || !formattedDate) {
        return DRAFT_QUOTE_LABEL;
    }

    return `Q-${dealerId}-${formattedDate}`;
};
